import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Alert,
    Tooltip,
    IconButton
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    PlayArrow as AnalysisIcon,
    Storage as BatchIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    Schedule as PendingIcon,
    TrendingUp as TrendingIcon
} from '@mui/icons-material';
import axiosWithAuth from '../../utils/axiosWithAuth';
import * as casService from '../../services/casService';

const AdminQuickView = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [recentAnalysis, setRecentAnalysis] = useState([]);
    const [recentBatches, setRecentBatches] = useState([]);

    const fetchQuickViewData = async () => {
        try {
            setLoading(true);
            setError('');

            // Fetch recent analysis triggers and batch history
            const [batchData] = await Promise.all([
                casService.getBatchJobs({ limit: 3, sort: 'created_at', order: 'desc' })
            ]);

            // Process batch data
            setRecentBatches(batchData.batch_jobs || batchData || []);

            // For analysis triggers, we'll get recent properties with last_cycle_triggered_at
            // This is a simplified approach - in a real implementation, you might want a dedicated endpoint
            try {
                const propertiesResponse = await axiosWithAuth.get('/cas/properties/admin/all');
                const properties = propertiesResponse.data || [];

                // Filter and sort properties by last analysis trigger
                const recentAnalysisData = properties
                    .filter(prop => prop.last_cycle_triggered_at)
                    .sort((a, b) => new Date(b.last_cycle_triggered_at) - new Date(a.last_cycle_triggered_at))
                    .slice(0, 3);

                setRecentAnalysis(recentAnalysisData);
            } catch (propError) {
                console.warn('Could not fetch properties for analysis triggers:', propError);
                setRecentAnalysis([]);
            }

        } catch (err) {
            console.error('Error fetching quick view data:', err);
            setError('Failed to load quick view data: ' + (err.response?.data?.detail || err.message));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchQuickViewData();
    }, []);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffHours / 24);

        if (diffHours < 1) return 'Just now';
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };

    const getBatchStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
            case 'success':
                return <SuccessIcon color="success" fontSize="small" />;
            case 'failed':
            case 'error':
                return <ErrorIcon color="error" fontSize="small" />;
            case 'processing':
            case 'running':
                return <PendingIcon color="warning" fontSize="small" />;
            default:
                return <PendingIcon color="default" fontSize="small" />;
        }
    };

    const getBatchStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
            case 'success':
                return 'success';
            case 'failed':
            case 'error':
                return 'error';
            case 'processing':
            case 'running':
                return 'warning';
            default:
                return 'default';
        }
    };

    const getBatchTypeIcon = (processingMode) => {
        switch (processingMode?.toLowerCase()) {
            case 'openai_batch':
                return '🤖';
            case 'sequential':
                return '📝';
            default:
                return '⚙️';
        }
    };

    if (loading) {
        return (
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
                    <CircularProgress />
                </Box>
            </Paper>
        );
    }

    return (
        <Paper sx={{ p: 2, mb: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6" component="h2">
                    Admin Quick View
                </Typography>
                <Tooltip title="Refresh data">
                    <IconButton onClick={fetchQuickViewData} size="small">
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={2}>
                {/* Recent Analysis Triggers */}
                <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                        <CardContent>
                            <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                                <AnalysisIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Recent Analysis Triggers
                                </Typography>
                            </Box>

                            {recentAnalysis.length === 0 ? (
                                <Typography variant="body2" color="textSecondary">
                                    No recent analysis triggers found
                                </Typography>
                            ) : (
                                <Box>
                                    {recentAnalysis.map((analysis, index) => (
                                        <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                <Box>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {analysis.friendly_name || analysis.gsc_property_name || 'Unknown Property'}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {formatDate(analysis.last_cycle_triggered_at)}
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    size="small"
                                                    label="Analysis"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Recent Batch Operations */}
                <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                        <CardContent>
                            <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                                <BatchIcon color="secondary" sx={{ mr: 1 }} />
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Recent Batch Operations
                                </Typography>
                            </Box>

                            {recentBatches.length === 0 ? (
                                <Typography variant="body2" color="textSecondary">
                                    No recent batch operations found
                                </Typography>
                            ) : (
                                <Box>
                                    {recentBatches.map((batch, index) => (
                                        <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                <Box>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {getBatchTypeIcon(batch.processing_mode)} {batch.processing_mode || 'Unknown'}
                                                        </Typography>
                                                        {getBatchStatusIcon(batch.status)}
                                                    </Box>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {formatDate(batch.created_at)}
                                                    </Typography>
                                                    {(batch.total_items || batch.processed_items) && (
                                                        <Typography variant="caption" color="textSecondary" display="block">
                                                            {batch.processed_items || 0}/{batch.total_items || '?'} items
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
                                                    <Chip
                                                        size="small"
                                                        label={batch.status || 'Unknown'}
                                                        color={getBatchStatusColor(batch.status)}
                                                        variant="outlined"
                                                    />
                                                    {batch.openai_batch_id && (
                                                        <Typography variant="caption" fontFamily="monospace">
                                                            {batch.openai_batch_id.slice(0, 8)}...
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default AdminQuickView;
